"use client";
import { Badge, Button, UncontrolledTooltip } from "reactstrap";

const TableItem = ({ campaign, handleShow, handleDelete }) => {
  const user = campaign?.user ?? null;

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: "secondary", text: "Draft" },
      need_approve: { color: "warning", text: "Needs Approval" },
      active: { color: "success", text: "Active" },
      inactive: { color: "secondary", text: "Inactive" },
      suspended: { color: "warning", text: "Suspended" },
      paused: { color: "info", text: "Paused" },
      completed: { color: "primary", text: "Completed" },
      cancelled: { color: "danger", text: "Cancelled" },
    };

    const config = statusConfig[status] || {
      color: "secondary",
      text: "Unknown",
    };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  const formatPrice = (price) => {
    const n = Number(price);
    const safe = Number.isFinite(n) ? n : 0;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(safe);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return "";
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  const isNeedingApproval = campaign.status === "need_approve";

  return (
    <tr className={isNeedingApproval ? "table-warning" : ""}>
      <td className="campaign_id">
        <div className="d-flex align-items-center">
          <div className="flex-shrink-0">
            <span className="fw-semibold">#{campaign.id}</span>
            {isNeedingApproval && (
              <div className="mt-1">
                <Badge color="warning" className="badge-soft-warning">
                  <i className="ri-time-line me-1"></i>
                  Pending
                </Badge>
              </div>
            )}
          </div>
        </div>
      </td>

      <td className="title">
        <div className="d-flex align-items-center">
          {campaign.image_url && (
            <div className="flex-shrink-0 me-3">
              <img
                src={
                  "https://api.tafaseelapp.com/storage/" + campaign.image_url ||
                  "/placeholder.svg"
                }
                alt={campaign.title}
                className="avatar-sm rounded"
                style={{ width: "40px", height: "40px", objectFit: "cover" }}
              />
            </div>
          )}
          <div className="flex-grow-1">
            <h6 className="mb-1">{truncateText(campaign.title, 30)}</h6>
            <p className="text-muted mb-0 small">
              {truncateText(campaign.description, 40)}
            </p>
          </div>
        </div>
      </td>

      <td className="business">
        <div>
          {user ? (
            <>
              <h6 className="mb-1">{user.name ?? "—"}</h6>
              {user.company_name && (
                <p className="text-muted mb-0 small">{user.company_name}</p>
              )}
              <p className="text-muted mb-0 small">{user.email ?? "—"}</p>
            </>
          ) : (
            <span className="text-muted">No business user</span>
          )}
        </div>
      </td>

      <td className="category">
        {campaign.category ? (
          <Badge color="light" className="text-dark">
            {campaign.category.name}
          </Badge>
        ) : (
          <span className="text-muted">No Category</span>
        )}
      </td>

      <td className="price">
        <span className="fw-semibold text-success">
          {formatPrice(campaign.price)}
        </span>
      </td>

      <td className="campaign_date">
        <span>{formatDate(campaign.campaign_date)}</span>
      </td>

      <td className="location">
        <div className="d-flex align-items-center">
          <i className="ri-map-pin-line text-muted me-1"></i>
          <span>{truncateText(campaign.location, 20)}</span>
        </div>
      </td>

      <td className="applications">
        <div className="d-flex gap-1">
          <Badge color="primary">{campaign.applications_count || 0}</Badge>
          {campaign.pending_applications > 0 && (
            <Badge color="warning">{campaign.pending_applications} P</Badge>
          )}
          {campaign.approved_applications > 0 && (
            <Badge color="success">{campaign.approved_applications} A</Badge>
          )}
        </div>
      </td>

      <td className="status">{getStatusBadge(campaign.status)}</td>

      <td className="views">
        <div className="d-flex align-items-center">
          <i className="ri-eye-line text-muted me-1"></i>
          <span>{campaign.views || 0}</span>
        </div>
      </td>

      <td className="created_at">
        <span>{formatDate(campaign.created_at)}</span>
      </td>

      <td>
        <div className="d-flex gap-2">
          <div className="show">
            <Button
              color="primary"
              size="sm"
              id={`show-${campaign.id}`}
              onClick={() => handleShow(campaign)}
              className="btn-icon"
            >
              <i className="ri-eye-fill"></i>
            </Button>
            <UncontrolledTooltip placement="top" target={`show-${campaign.id}`}>
              View Details
            </UncontrolledTooltip>
          </div>

          <div className="remove">
            <Button
              color="danger"
              size="sm"
              id={`delete-${campaign.id}`}
              onClick={() => handleDelete(campaign.id)}
              className="btn-icon"
            >
              <i className="ri-delete-bin-fill"></i>
            </Button>
            <UncontrolledTooltip
              placement="top"
              target={`delete-${campaign.id}`}
            >
              Delete Campaign
            </UncontrolledTooltip>
          </div>
        </div>
      </td>
    </tr>
  );
};

export default TableItem;
